import React, { useEffect } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { ButtonIcon } from "../../../components/Button/Button"
import {
  faClose,
  faLayerGroup,
  faPlus,
  faTrash,
  faListCheck,
  faTag,
  faCircleInfo,
  faMoneyBillTransfer,
  faSliders,
} from "@fortawesome/free-solid-svg-icons"
import InputField from "../../../components/InputField/InputField"
import GetSpace from "../../../utils/space"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { SelectionEntity } from "../../../Objects/Selection/SelectionEntity"
import { useSaveSelection } from "../../../hooks/Selection/useSaveSelection"
import { useGetSelection } from "../../../hooks/Selection/useGetSelection"
import { useRemoveSelection } from "../../../hooks/Selection/useRemoveSelection"
import Button from "../../../components/Button/Button"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import { SelectionOptionEntity } from "../../../Objects/Selection/SelectionOptionEntity"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useRegister } from "../../../hooks/Register/useRegister"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import { Permissions } from "../../../Enum/PermissionsType"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { CanView } from "../../../components/CanView/CanView"
import { useCreateSelection } from "../../../hooks/Selection/useCreateSelection"
import { useApplySelection } from "../../../hooks/Selection/useApplySelection"
import { CopyDraftAlert } from "../../../components/CopyDraftAlert/CopyDraftAlert"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { useTranslation } from "react-i18next"
import { useError } from "../../../context/useErrorContext"
import { AssociationsPopup } from "../../../components/Associations/AssociationsPopup"
import { comboMemory, drinksMemory, mealsMemory } from "../../../Objects/Memory/Memory"
import { resolveSelectionAssociations } from "../../../components/Associations/associationResolvers"
import { ImageLibraryPicker } from "../../../components/ImageLibrary/ImageLibraryPicker"
import { getImageLibraryFile } from "../../../utils/imageLibraryFile"
import { SelectionApplyPopup } from "./SelectionApplyPopup"
import { useGetMeals } from "../../../hooks/Meals/useGetMeals"
import { useGetDrinks } from "../../../hooks/Drinks/useGetDrinks"
import { useGetCombo } from "../../../hooks/Combo/useGetCombo"

interface SelectionPupopProps {
  title: string
  selection?: SelectionEntity
  onClose: () => void
  onCopy?: () => void
}

export const SelectionPupop: React.FC<SelectionPupopProps> = ({ title, selection, onClose, onCopy }) => {
  const { t } = useTranslation("selection")

  const { setError } = useError()

  const canEdite = !usePermission(Permissions.CanEditeAddtionals)

  const isCopiedDraft = selection?.id === -1

  const [showAssociations, setShowAssociations] = React.useState(false)

  const [enableApplyPopup, setEnableApplyPopup] = React.useState(false)

  const [enableRemoveApplyPopup, setEnableRemoveApplyPopup] = React.useState(false)

  const { handleSaveSelection } = useSaveSelection()

  const { handleGetSelection } = useGetSelection()

  const { handleApplySelection, handleRemoveSelectionApply } = useApplySelection()

  const { handleGetMeals } = useGetMeals()

  const { handleGetDrinks } = useGetDrinks()

  const { handleGetCombo } = useGetCombo()

  const { handleRemoveSelection } = useRemoveSelection()

  const { setNewConfirmation } = useConfirmation()

  const {
    name,
    setName,
    description,
    setDescription,
    status,
    setStatus,
    options,
    setOptions,
    allowMultiple,
    setAllowMultiple,
    isRequired,
    setIsRequired,
    requiredOptionsCount,
    setRequiredOptionsCount,
    freeOptionsLimit,
    setFreeOptionsLimit,
    hasMaximumSelectionsLimit,
    setHasMaximumSelectionsLimit,
    maximumSelectionsLimit,
    setMaximumSelectionsLimit,
    handleInitialValue,
    handleSubmitForm,
  } = useCreateSelection()

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  useEffect(() => {
    handleInitialValue(selection)
  }, [selection])

  const refreshProductAssociations = () => {
    handleGetSelection(() => {})
    handleGetMeals(() => {})
    handleGetDrinks(() => {})
    handleGetCombo(() => {})
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={title}
              icon={faLayerGroup}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  {selection && selection.id !== -1 && (
                    <div style={{ width: "100px", marginRight: "5px" }}>
                      <GenericDropDown
                        data={Object.mapEnumToStrings(EEnableStatusType, { exclude: [EEnableStatusType.All] })}
                        initialState={status}
                        handlerFuncion={(e) => setStatus(e.target.selectedIndex as EEnableStatusType)}
                        disable={canEdite}
                        descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.select.select"
                      />
                    </div>
                  )}

                  <PopupHelpButton
                    helpKey="pages.products.selection.selectionpupop.1"
                    variant="global"
                    title={t("helpPopup:global.selection.title")}
                    description={t("helpPopup:global.selection.description")}
                    bullets={t("helpPopup:global.selection.bullets", { returnObjects: true }) as string[]}
                  />

                  <GetSpace right={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.button.onClose"
                  />
                </div>
              }
            />

            {isCopiedDraft && <CopyDraftAlert />}

            <form
              onSubmit={(event) => {
                event.preventDefault()

                const invalidOptions = options
                  .map((option, optionIndex) => ({ option, optionIndex }))
                  .filter(({ option }) => !option.isFree && Number(option.value) <= 0)

                if (invalidOptions.length > 0) {
                  const invalidOptionsNumbers = invalidOptions.map(({ optionIndex }) => optionIndex + 1).join(", ")
                  setError(
                    t("label_non_free_zero_value_error_title"),
                    t("label_non_free_zero_value_error_description", { options: invalidOptionsNumbers }),
                  )
                  return
                }

                const requiredOptionsCountNumber = allowMultiple ? Number(requiredOptionsCount) : 1

                const freeOptionsLimitNumber = allowMultiple ? Number(freeOptionsLimit) : 0

                const maximumSelectionsLimitNumber = allowMultiple ? Number(maximumSelectionsLimit) : 0

                if (requiredOptionsCountNumber < 0 || freeOptionsLimitNumber < 0 || maximumSelectionsLimitNumber < 0) {
                  setError(t("label_selection_limit_error_title"), t("label_selection_limit_negative_description"))
                  return
                }

                const validOptionsCount = options.filter((option) => option.name.trim().length > 0).length

                const validOptionsCapacity = options
                  .filter((option) => option.name.trim().length > 0)
                  .reduce(
                    (sum, option) =>
                      sum +
                      (allowMultiple && option.allowMultiple ? Math.max(1, Number(option.maximumSelectionsLimit)) : 1),
                    0,
                  )

                if (
                  (isRequired && requiredOptionsCountNumber > validOptionsCapacity) ||
                  freeOptionsLimitNumber > validOptionsCapacity
                ) {
                  setError(
                    t("label_selection_limit_error_title"),
                    t("label_selection_limit_options_capacity_description"),
                  )
                  return
                }

                if (!allowMultiple && (requiredOptionsCountNumber > 1 || freeOptionsLimitNumber > 0)) {
                  setError(t("label_selection_limit_error_title"), t("label_single_choice_limit_description"))
                  return
                }

                if (allowMultiple && hasMaximumSelectionsLimit && maximumSelectionsLimitNumber < 1) {
                  setError(
                    t("label_selection_limit_error_title"),
                    t("label_maximum_selection_limit_positive_description"),
                  )
                  return
                }

                if (allowMultiple && hasMaximumSelectionsLimit && maximumSelectionsLimitNumber > validOptionsCapacity) {
                  setError(
                    t("label_selection_limit_error_title"),
                    t("label_maximum_selection_limit_options_capacity_description"),
                  )
                  return
                }

                if (
                  allowMultiple &&
                  options.some(
                    (option) =>
                      option.name.trim().length > 0 &&
                      option.allowMultiple &&
                      Number(option.maximumSelectionsLimit) < 1,
                  )
                ) {
                  setError(t("label_selection_limit_error_title"), t("label_option_maximum_limit_positive_description"))
                  return
                }

                if (
                  allowMultiple &&
                  hasMaximumSelectionsLimit &&
                  options.some(
                    (option) =>
                      option.name.trim().length > 0 &&
                      option.allowMultiple &&
                      Number(option.maximumSelectionsLimit) > maximumSelectionsLimitNumber,
                  )
                ) {
                  setError(
                    t("label_selection_limit_error_title"),
                    t("label_option_limit_maximum_selection_limit_description"),
                  )
                  return
                }

                if (
                  isRequired &&
                  allowMultiple &&
                  hasMaximumSelectionsLimit &&
                  requiredOptionsCountNumber > maximumSelectionsLimitNumber
                ) {
                  setError(
                    t("label_selection_limit_error_title"),
                    t("label_required_limit_maximum_selection_limit_description"),
                  )
                  return
                }

                if (
                  allowMultiple &&
                  hasMaximumSelectionsLimit &&
                  freeOptionsLimitNumber > maximumSelectionsLimitNumber
                ) {
                  setError(
                    t("label_selection_limit_error_title"),
                    t("label_free_limit_maximum_selection_limit_description"),
                  )
                  return
                }

                const model = handleSubmitForm(selection?.id)
                handleSaveSelection(model, () => {
                  onClose()
                  handleGetSelection(() => {})
                })
              }}
            >
              <section className="p-2 mt-2">
                <InputField
                  type="text"
                  icon={faTag}
                  value={name}
                  placeholder={t("label_name_placeholder")}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disable={canEdite}
                  descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.input.namePlaceholder"
                />

                <GetSpace top={8} />

                <InputField
                  type="text"
                  icon={faCircleInfo}
                  value={description}
                  placeholder={t("label_description_placeholder")}
                  onChange={(e) => setDescription(e.target.value)}
                  disable={canEdite}
                  descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.input.descriptionPlaceholder"
                />

                <div className={defaultStyle.divisionBar}></div>

                <div className="grid gap-3 mb-3">
                  <div className="rounded-lg border border-sky-100 bg-sky-50/40 p-3 shadow-sm">
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <FontAwesomeIcon icon={faSliders} className="text-sky-600" />
                        <p className="min-w-0 text-sm font-black text-slate-700">
                          {t("label_multiple_selection_group")}
                        </p>
                      </div>

                      <PopupHelpButton
                        title={t("label_multiple_selection_help_title")}
                        description={t("label_multiple_selection_help_description")}
                        bullets={t("label_multiple_selection_help_bullets", { returnObjects: true }) as string[]}
                      />
                    </div>

                    <div className="grid gap-2">
                      <SimpleToggle
                        title={t("label_allow_multiple")}
                        value={allowMultiple}
                        onChange={setAllowMultiple}
                        disabled={canEdite}
                        descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.toggle.allowMultiple"
                      />

                      {allowMultiple && (
                        <>
                          <SimpleToggle
                            title={t("label_has_maximum_selections_limit")}
                            value={hasMaximumSelectionsLimit}
                            onChange={setHasMaximumSelectionsLimit}
                            disabled={canEdite}
                            descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.toggle.hasMaximumSelectionsLimit"
                          />

                          {hasMaximumSelectionsLimit && (
                            <InputField
                              type="number"
                              icon={faSliders}
                              value={maximumSelectionsLimit}
                              placeholder={t("label_maximum_selections_limit_placeholder")}
                              onChange={(e) => setMaximumSelectionsLimit(e.target.value)}
                              required
                              disable={canEdite}
                              descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.input.maximumSelectionsLimitPlaceholder"
                            />
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="rounded-lg border border-orange-100 bg-orange-50/40 p-3 shadow-sm">
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <FontAwesomeIcon icon={faListCheck} className="text-orange-600" />
                        <p className="min-w-0 text-sm font-black text-slate-700">
                          {t("label_required_selection_group")}
                        </p>
                      </div>

                      <PopupHelpButton
                        title={t("label_required_selection_help_title")}
                        description={t("label_required_selection_help_description")}
                        bullets={t("label_required_selection_help_bullets", { returnObjects: true }) as string[]}
                      />
                    </div>

                    <div className="grid gap-2">
                      <SimpleToggle
                        title={t("label_required_selection")}
                        value={isRequired}
                        onChange={setIsRequired}
                        disabled={canEdite}
                        descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.toggle.requiredSelection"
                      />

                      {isRequired && allowMultiple && (
                        <InputField
                          type="number"
                          icon={faListCheck}
                          value={requiredOptionsCount}
                          placeholder={t("label_required_options_count_placeholder")}
                          onChange={(e) => setRequiredOptionsCount(e.target.value)}
                          required
                          disable={canEdite}
                          descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.input.requiredOptionsCountPlaceholder"
                        />
                      )}
                    </div>
                  </div>

                  {allowMultiple && (
                    <div className="rounded-lg border border-lime-100 bg-lime-50/40 p-3 shadow-sm">
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <FontAwesomeIcon icon={faMoneyBillTransfer} className="text-lime-700" />
                          <p className="min-w-0 text-sm font-black text-slate-700">{t("label_free_choices_group")}</p>
                        </div>

                        <PopupHelpButton
                          title={t("label_free_choices_help_title")}
                          description={t("label_free_choices_help_description")}
                          bullets={t("label_free_choices_help_bullets", { returnObjects: true }) as string[]}
                        />
                      </div>

                      <InputField
                        type="number"
                        icon={faMoneyBillTransfer}
                        value={freeOptionsLimit}
                        placeholder={t("label_free_options_limit_placeholder")}
                        onChange={(e) => setFreeOptionsLimit(e.target.value)}
                        disable={canEdite}
                        descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.input.freeOptionsLimitPlaceholder"
                      />
                    </div>
                  )}
                </div>

                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <div className="rounded-lg border border-orange-100 bg-orange-50/40 p-3 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faListCheck} className="text-orange-600" />
                        <p className="text-sm font-black text-slate-700">{t("label_options")}</p>
                      </div>

                      <div className="flex items-center gap-2">
                        <PopupHelpButton
                          helpKey="pages.products.selection.selectionpupop.2"
                          title={t("helpPopup:sections.selection.options.title")}
                          description={t("helpPopup:sections.selection.options.description")}
                          bullets={
                            t("helpPopup:sections.selection.options.bullets", { returnObjects: true }) as string[]
                          }
                        />
                        <ButtonIcon
                          typeButton="button"
                          sizeNumber={SIZE.buttonExpandIcon}
                          icon={faPlus}
                          bgColor={THEME.colors.buttonBGPlusColor}
                          iconColor={THEME.colors.buttonFGPlusColor}
                          onClick={() => setOptions((prev) => [...prev, new SelectionOptionEntity({ name: "" })])}
                          disabled={canEdite}
                          descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.button.setOptionsPrevPrevNewSelectionOptionEntity"
                        />
                      </div>
                    </div>

                    {options.map((option, index) => (
                      <Animate key={index} type={AnimationTypes.fadeInTraslate} duration={1.5 + index * 0.2 + "s"}>
                        <div className="rounded-lg border border-slate-200 bg-white p-3 mb-3 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-700 text-xs font-black flex items-center justify-center">
                                {index + 1}
                              </span>
                              <span className="text-xs font-black uppercase tracking-wide text-slate-600">
                                {t("label_option")}
                              </span>
                            </div>

                            <CanView permission={Permissions.CanEditeAddtionals}>
                              <button
                                type="button"
                                onClick={() => setOptions((prev) => prev.filter((_, itemIndex) => itemIndex !== index))}
                                disabled={canEdite}
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition-all text-xs font-bold"
                                data-interaction-description-key="interactionDescriptions:pages.products.selection.selectionPupop.button.setOptionsPrevPrevFilterItemIndexItemIndexIndex"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                                {t("buttons:button_remove")}
                              </button>
                            </CanView>
                          </div>

                          <ImageLibraryPicker
                            disabled={canEdite}
                            imageProfile="additional"
                            maxSelection={1}
                            selectedIds={[]}
                            selectedUrls={option.iconUrl ? [option.iconUrl] : []}
                            onChange={async (ids, urls) => {
                              const updateOptionImage = (icon: File | null) =>
                                setOptions((prev) =>
                                  prev.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? new SelectionOptionEntity({ ...item, iconUrl: urls[0] ?? "", icon })
                                      : item,
                                  ),
                                )

                              if (ids.length === 0) {
                                updateOptionImage(
                                  urls.length === 0 && option.iconUrl
                                    ? new File(["remove-image"], "__remove_icon__", { type: "text/plain" })
                                    : null,
                                )
                                return
                              }

                              updateOptionImage(await getImageLibraryFile(ids[0], urls[0]))
                            }}
                          />

                          <GetSpace top={8} />

                          <InputField
                            type="text"
                            icon={faListCheck}
                            value={option.name}
                            placeholder={t("label_option_example_placeholder")}
                            onChange={(e) =>
                              setOptions((prev) =>
                                prev.map((item, itemIndex) =>
                                  itemIndex === index
                                    ? new SelectionOptionEntity({ ...item, name: e.target.value })
                                    : item,
                                ),
                              )
                            }
                            required
                            disable={canEdite}
                            descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.input.optionExamplePlaceholder"
                          />

                          <GetSpace top={8} />

                          <SimpleToggle
                            title={t("order:label_free")}
                            value={option.isFree}
                            disabled={canEdite}
                            onChange={(value) =>
                              setOptions((prev) =>
                                prev.map((item, itemIndex) =>
                                  itemIndex === index
                                    ? new SelectionOptionEntity({
                                        ...item,
                                        isFree: value,
                                        value: value ? 0 : item.value,
                                      })
                                    : item,
                                ),
                              )
                            }
                            descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.toggle.free"
                          />

                          {!option.isFree && (
                            <>
                              <GetSpace top={8} />

                              <InputField
                                type="number"
                                icon={faMoneyBillTransfer}
                                value={String(option.value ?? 0)}
                                placeholder={t("label_additional_value_placeholder")}
                                onChange={(e) =>
                                  setOptions((prev) =>
                                    prev.map((item, itemIndex) =>
                                      itemIndex === index
                                        ? new SelectionOptionEntity({ ...item, value: Number(e.target.value) })
                                        : item,
                                    ),
                                  )
                                }
                                required
                                disable={canEdite}
                                descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.input.additionalValuePlaceholder"
                              />
                            </>
                          )}

                          {allowMultiple && (
                            <>
                              <GetSpace top={8} />

                              <div className="rounded-lg border border-sky-100 bg-sky-50/50 p-2">
                                <SimpleToggle
                                  title={t("label_option_allow_multiple")}
                                  value={option.allowMultiple}
                                  disabled={canEdite}
                                  onChange={(value) =>
                                    setOptions((prev) =>
                                      prev.map((item, itemIndex) =>
                                        itemIndex === index
                                          ? new SelectionOptionEntity({
                                              ...item,
                                              allowMultiple: value,
                                              maximumSelectionsLimit: value
                                                ? Math.max(1, item.maximumSelectionsLimit ?? 1)
                                                : 1,
                                            })
                                          : item,
                                      ),
                                    )
                                  }
                                  descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.toggle.optionAllowMultiple"
                                />

                                {option.allowMultiple && (
                                  <>
                                    <GetSpace top={8} />

                                    <InputField
                                      type="number"
                                      icon={faSliders}
                                      value={String(option.maximumSelectionsLimit ?? 1)}
                                      placeholder={t("label_option_maximum_selections_limit_placeholder")}
                                      onChange={(e) =>
                                        setOptions((prev) =>
                                          prev.map((item, itemIndex) =>
                                            itemIndex === index
                                              ? new SelectionOptionEntity({
                                                  ...item,
                                                  maximumSelectionsLimit: Number(e.target.value),
                                                })
                                              : item,
                                          ),
                                        )
                                      }
                                      required
                                      disable={canEdite}
                                      descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.input.optionMaximumSelectionsLimitPlaceholder"
                                    />
                                  </>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </Animate>
                    ))}

                    {options.length === 0 && (
                      <div className="rounded-lg border border-dashed border-orange-300 bg-white/80 p-4 text-center text-xs text-orange-700 font-semibold">
                        {t("label_add_option_hint")} <span className="font-black">+</span>.
                      </div>
                    )}
                  </div>
                </Animate>
              </section>

              <div className={defaultStyle.divisionBar}></div>

              <section className={defaultStyle.buttonsController}>
                <CanView permission={Permissions.CanEditeAddtionals}>
                  <Button
                    label={t("buttons:button_save")}
                    typeButton="submit"
                    type="primary"
                    size="medium"
                    bgColor={THEME.colors.buttonBGSaveColor}
                    descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.button.save"
                  />
                </CanView>

                {selection && selection.id !== -1 && (
                  <>
                    <GetSpace right={5} />

                    <Button
                      label={t("buttons:button_register")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGRegisterColor}
                      onClick={handleEnableRegister}
                      descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.button.register"
                    />

                    <GetSpace right={5} />

                    <Button
                      label={t("buttons:button_view_associations")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGPlusColor}
                      onClick={() => setShowAssociations(true)}
                      descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.button.viewAssociations"
                    />

                    {onCopy && (
                      <>
                        <GetSpace right={5} />

                        <CanView permission={Permissions.CanEditeAddtionals}>
                          <Button
                            label={t("buttons:button_copy")}
                            typeButton="button"
                            type="primary"
                            size="medium"
                            bgColor={THEME.colors.buttonBGPlusColor}
                            onClick={onCopy}
                            descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.button.copy"
                          />
                        </CanView>
                      </>
                    )}

                    <CanView permission={Permissions.CanEditeAddtionals}>
                      <GetSpace right={5} />

                      <Button
                        label={t("buttons:button_remove")}
                        typeButton="button"
                        type="primary"
                        size="medium"
                        iconColor={THEME.colors.buttonFGRemoveColor}
                        bgColor={THEME.colors.buttonBGRemoveColor}
                        onClick={() =>
                          setNewConfirmation(t("buttons:button_remove"), t("label_confirm_remove"), async () => {
                            await handleRemoveSelection(selection, () => handleGetSelection(onClose))
                          })
                        }
                        descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.button.remove"
                      />
                    </CanView>
                  </>
                )}
              </section>

              <GetSpace top={5} />

              <section className={defaultStyle.buttonsController}>
                {selection && selection.id !== -1 && (
                  <CanView permission={Permissions.CanEditeAddtionals}>
                    <>
                      <GetSpace right={5} />

                      <Button
                        label={t("buttons:button_apply")}
                        typeButton="button"
                        type="primary"
                        size="medium"
                        bgColor={THEME.colors.buttonBGRegisterColor}
                        onClick={() => setEnableApplyPopup(true)}
                        descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.button.apply"
                      />

                      <GetSpace right={5} />

                      <Button
                        label={t("button_remove_apply_automatic")}
                        typeButton="button"
                        type="primary"
                        size="medium"
                        bgColor={THEME.colors.buttonBGCloseColor}
                        onClick={() => setEnableRemoveApplyPopup(true)}
                        descriptionKey="interactionDescriptions:pages.products.selection.selectionPupop.button.removeApplyAutomatic"
                      />
                    </>
                  </CanView>
                )}
              </section>
            </form>

            {isRegisterEnable && selection && (
              <ViewRegister id={selection.id} typeEntity="SelectionEntityId" onClose={handleDisableRegister} />
            )}

            {showAssociations && selection && (
              <AssociationsPopup
                title={t("general:label_related_title", { name: selection.name })}
                emptyMessage={t("general:label_related_empty")}
                onClose={() => setShowAssociations(false)}
                items={resolveSelectionAssociations(
                  selection,
                  mealsMemory.listData,
                  drinksMemory.listData,
                  comboMemory.listData,
                )}
              />
            )}

            {enableApplyPopup && selection && (
              <SelectionApplyPopup
                title={t("label_title_apply_automatic")}
                actionLabel={t("buttons:button_apply")}
                helpKey="pages.products.selection.selectionpupop.3"
                helpTitle={t("helpPopup:sections.selection.applyAutomatic.title")}
                helpDescription={t("helpPopup:sections.selection.applyAutomatic.description")}
                helpBullets={
                  t("helpPopup:sections.selection.applyAutomatic.bullets", { returnObjects: true }) as string[]
                }
                onClose={() => setEnableApplyPopup(false)}
                onConfirm={async (request) => {
                  await handleApplySelection(selection.id, request, () => {
                    refreshProductAssociations()
                    setEnableApplyPopup(false)
                  })
                }}
              />
            )}

            {enableRemoveApplyPopup && selection && (
              <SelectionApplyPopup
                title={t("label_title_remove_apply_automatic")}
                actionLabel={t("button_remove_apply_automatic")}
                helpKey="pages.products.selection.selectionpupop.4"
                helpTitle={t("helpPopup:sections.selection.removeAutomatic.title")}
                helpDescription={t("helpPopup:sections.selection.removeAutomatic.description")}
                helpBullets={
                  t("helpPopup:sections.selection.removeAutomatic.bullets", { returnObjects: true }) as string[]
                }
                onClose={() => setEnableRemoveApplyPopup(false)}
                onConfirm={async (request) => {
                  await handleRemoveSelectionApply(selection.id, request, () => {
                    refreshProductAssociations()
                    setEnableRemoveApplyPopup(false)
                  })
                }}
              />
            )}
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
